/**
 * ============================================================================
 * KbLinkEditorDialog — Modal robusto para inserir/editar links no editor KB.
 * ----------------------------------------------------------------------------
 * Padrão GitBook: uma única interface trata:
 *   - Link EXTERNO (http/https/mailto/tel)
 *   - Link INTERNO (wiki-link) navegando Space → Página → Seção (heading)
 *
 * Comportamento:
 *   - Colar/digitar URL no input principal desabilita o navegador e trata
 *     como link externo.
 *   - Digitar texto sem cara de URL faz busca por título de página em todos
 *     os spaces (usa kb_search_pages_for_link).
 *   - Colunas: Spaces | Páginas | Seções (heading) — estilo Miller.
 *   - Modo edição: pré-preenche a partir do `initial` (external ou page).
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v1.0.1 - Layout premium (max-w-5xl + 85dvh), colunas
 *            com ScrollArea funcional (removida altura fixa 320px), seções em
 *            line-clamp-2 e tooltip nativo em títulos longos.
 *   RELEASE: 2026-07-09 v1.0.0 - Versão inicial.
 * ============================================================================
 */

import { useEffect, useMemo, useState } from "react";
import {
  Search, FileText, Loader2, Globe2, Layers, Hash, ExternalLink, X, Home,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useKbSpaces, useKbPage } from "@/hooks/kb/useKb";
import { useKbSearchPagesForLink, type KbLinkSearchResult } from "@/hooks/kb/useKbSearchPagesForLink";
import { extractHeadings, type DocHeading } from "@/hooks/kb/useDocHeadings";
import type { LinkEditorInitial, LinkEditorResult } from "@/lib/kb/pageLinkPicker";

// ---------------------------------------------------------------------------
// Utilidades de detecção/validação de URL externa
// ---------------------------------------------------------------------------

/** Protocolos aceitos para link externo (whitelist). */
const ALLOWED_PROTOCOLS = ["http:", "https:", "mailto:", "tel:"];

/**
 * Determina se o texto parece uma URL externa. Aceita:
 *  - `http(s)://...`
 *  - `mailto:foo@bar`
 *  - `tel:+55...`
 *  - `www.foo.com` (adiciona https:// depois)
 *  - `foo.com/algo` (contém `.` sem espaço e sem `/` no início)
 *  - `/rota-relativa`
 */
function looksLikeUrl(input: string): boolean {
  const s = input.trim();
  if (!s) return false;
  if (/^(https?:|mailto:|tel:)/i.test(s)) return true;
  if (/^www\./i.test(s)) return true;
  if (s.startsWith("/")) return true;
  // hostname sem espaço com ponto interno (ex.: docs.foo.com)
  if (/^[^\s]+\.[^\s]{2,}(\/.*)?$/.test(s)) return true;
  return false;
}

/** Normaliza para uma URL final válida, ou retorna null se o protocolo não for permitido. */
function normalizeUrl(input: string): string | null {
  let s = input.trim();
  if (!s) return null;
  if (/^www\./i.test(s)) s = `https://${s}`;
  else if (!/^(https?:|mailto:|tel:|\/)/i.test(s) && /\./.test(s)) s = `https://${s}`;
  // Aceita caminho relativo direto
  if (s.startsWith("/")) return s;
  try {
    const u = new URL(s);
    if (!ALLOWED_PROTOCOLS.includes(u.protocol)) return null;
    return u.toString();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currentSpaceId?: string | null;
  initial: LinkEditorInitial;
  onSubmit: (result: LinkEditorResult) => void;
}

export function KbLinkEditorDialog({
  open, onOpenChange, currentSpaceId, initial, onSubmit,
}: Props) {
  // --- Estado principal ---------------------------------------------------
  const [urlInput, setUrlInput] = useState("");
  const [visibleText, setVisibleText] = useState("");
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<KbLinkSearchResult | null>(null);
  const [selectedAnchor, setSelectedAnchor] = useState<string | null>(null);
  const [pageSearch, setPageSearch] = useState("");

  const { data: spaces } = useKbSpaces();
  const { data: pages, isLoading: loadingPages } = useKbSearchPagesForLink(
    pageSearch,
    selectedSpaceId,
    30,
  );
  const { data: selectedPageFull } = useKbPage(selectedPage?.page_id ?? null);

  // Headings da página selecionada (recomputados quando content_json muda)
  const headings: DocHeading[] = useMemo(() => {
    if (!selectedPageFull) return [];
    return extractHeadings((selectedPageFull as any).content_json);
  }, [selectedPageFull]);

  // --- Reset ao abrir + pré-preenchimento do modo edição -------------------
  useEffect(() => {
    if (!open) return;
    // Defaults
    setPageSearch("");
    setSelectedAnchor(null);
    setSelectedPage(null);
    setSelectedSpaceId(currentSpaceId ?? null);

    if (initial.kind === "external") {
      setUrlInput(initial.href ?? "");
      setVisibleText(initial.text ?? "");
    } else if (initial.kind === "page") {
      setUrlInput("");
      setVisibleText(initial.text ?? initial.snapshotTitle ?? "");
      setSelectedSpaceId(initial.spaceId ?? currentSpaceId ?? null);
      setSelectedAnchor(initial.anchor ?? null);
      // Injeta uma stub para exibir a coluna de seções enquanto useKbPage carrega
      setSelectedPage({
        page_id: initial.pageId,
        space_id: initial.spaceId ?? currentSpaceId ?? "",
        space_name: "",
        collection_id: null,
        collection_name: null,
        title: initial.snapshotTitle ?? "…",
        slug: "",
        status: "",
        updated_at: "",
      });
    } else {
      setUrlInput("");
      setVisibleText(initial.text ?? "");
    }
  }, [open, initial, currentSpaceId]);

  // --- Modo do input principal --------------------------------------------
  const isExternalMode = looksLikeUrl(urlInput);
  const normalizedUrl = isExternalMode ? normalizeUrl(urlInput) : null;
  const canConfirm =
    (isExternalMode && !!normalizedUrl) ||
    (!isExternalMode && !!selectedPage?.page_id);

  // --- Ações ---------------------------------------------------------------
  const handleConfirm = () => {
    if (isExternalMode) {
      if (!normalizedUrl) return;
      onSubmit({ kind: "external", href: normalizedUrl, text: visibleText.trim() || undefined });
      onOpenChange(false);
      return;
    }
    if (!selectedPage?.page_id) return;
    const title = (visibleText.trim() || selectedPage.title || "Artigo");
    onSubmit({
      kind: "page",
      pageId: selectedPage.page_id,
      spaceId: selectedPage.space_id || selectedSpaceId || "",
      anchor: selectedAnchor,
      title,
    });
    onOpenChange(false);
  };

  const handleRemove = () => {
    onSubmit({ kind: "remove" });
    onOpenChange(false);
  };

  const showBrowser = !isExternalMode;
  const isEditingExisting = initial.kind !== "none";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[85dvh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-5 pb-4 border-b shrink-0">
          <DialogTitle className="text-base font-semibold">
            {isEditingExisting ? "Editar link" : "Inserir link"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Link para conteúdo interno (space, página ou seção) ou URL externa.
          </DialogDescription>
        </DialogHeader>

        {/* URL ou busca */}
        <div className="px-6 py-4 space-y-2 shrink-0 border-b">

          <Label htmlFor="kb-link-url" className="text-xs font-medium">
            URL ou conteúdo
          </Label>
          <div className="relative">
            {isExternalMode ? (
              <ExternalLink className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            ) : (
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            )}
            <Input
              id="kb-link-url"
              autoFocus
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://exemplo.com ou nome do artigo…"
              className="pl-8 h-9 text-sm"
            />
            {urlInput && (
              <button
                type="button"
                onClick={() => setUrlInput("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-accent text-muted-foreground"
                title="Limpar"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {isExternalMode && !normalizedUrl && (
            <p className="text-[11px] text-destructive">
              URL inválida. Aceito: http(s)://, mailto:, tel: ou caminho /relativo.
            </p>
          )}
        </div>

        {/* Navegador hierárquico (só para modo interno) */}
        {showBrowser && (
          <div className="flex-1 min-h-0 bg-muted/20">
            <div className="grid grid-cols-3 divide-x h-full">

              {/* Coluna 1: Spaces */}
              <BrowserColumn
                title="Spaces"
                icon={<Home className="h-3.5 w-3.5" />}
              >
                {(spaces ?? []).map((sp: any) => {
                  const active = sp.id === selectedSpaceId;
                  return (
                    <ColumnItem
                      key={sp.id}
                      active={active}
                      onClick={() => {
                        setSelectedSpaceId(sp.id);
                        setSelectedPage(null);
                        setSelectedAnchor(null);
                        setPageSearch("");
                      }}
                      icon={<Layers className="h-3.5 w-3.5 text-muted-foreground" />}
                      label={sp.name}
                      hasChildren
                    />
                  );
                })}
                {(!spaces || spaces.length === 0) && (
                  <EmptyMsg text="Sem spaces disponíveis" />
                )}
              </BrowserColumn>

              {/* Coluna 2: Páginas */}
              <BrowserColumn
                title="Páginas"
                icon={<FileText className="h-3.5 w-3.5" />}
                headerRight={
                  <div className="relative w-full max-w-[180px]">
                    <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      value={pageSearch}
                      onChange={(e) => setPageSearch(e.target.value)}
                      placeholder="Buscar…"
                      className="h-6 pl-6 text-[11px]"
                    />
                  </div>
                }
              >
                {!selectedSpaceId && <EmptyMsg text="Selecione um space" />}
                {selectedSpaceId && loadingPages && (
                  <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando…
                  </div>
                )}
                {selectedSpaceId && !loadingPages && (pages ?? []).length === 0 && (
                  <EmptyMsg text="Nenhuma página encontrada" />
                )}
                {selectedSpaceId && (pages ?? []).map((pg) => {
                  const active = pg.page_id === selectedPage?.page_id;
                  return (
                    <ColumnItem
                      key={pg.page_id}
                      active={active}
                      onClick={() => {
                        setSelectedPage(pg);
                        setSelectedAnchor(null);
                      }}
                      icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
                      label={pg.title}
                      hint={pg.collection_name ?? undefined}
                      hasChildren
                    />
                  );
                })}
              </BrowserColumn>

              {/* Coluna 3: Seções (headings) */}
              <BrowserColumn
                title="Seções"
                icon={<Hash className="h-3.5 w-3.5" />}
              >
                {!selectedPage && <EmptyMsg text="Selecione uma página" />}
                {selectedPage && (
                  <>
                    <ColumnItem
                      active={!selectedAnchor}
                      onClick={() => setSelectedAnchor(null)}
                      icon={<FileText className="h-3.5 w-3.5 text-muted-foreground" />}
                      label={`Início do artigo`}
                      hint="sem âncora"
                    />
                    {headings.map((h) => {
                      const active = selectedAnchor === h.id;
                      return (
                        <ColumnItem
                          key={h.id}
                          active={active}
                          onClick={() => setSelectedAnchor(h.id)}
                          icon={
                            <span className="text-[10px] font-mono text-muted-foreground w-6 text-center">
                              H{h.level}
                            </span>
                          }
                          label={h.text}
                          indent={Math.max(0, h.level - 1)}
                          clamp

                        />
                      );
                    })}
                    {headings.length === 0 && (
                      <EmptyMsg text="Sem seções nesta página" />
                    )}
                  </>
                )}
              </BrowserColumn>
            </div>
          </div>
        )}

        {/* Spacer quando modo externo (browser não é renderizado) */}
        {!showBrowser && <div className="flex-1 min-h-0" />}

        {/* Texto visível */}
        <div className="px-6 py-3 border-t shrink-0">
          <Label htmlFor="kb-link-text" className="text-xs font-medium">
            Texto visível <span className="text-muted-foreground">(opcional)</span>
          </Label>
          <Input
            id="kb-link-text"
            value={visibleText}
            onChange={(e) => setVisibleText(e.target.value)}
            placeholder={
              isExternalMode
                ? "Ex.: Documentação oficial"
                : selectedPage?.title ?? "Rótulo do link"
            }
            className="h-9 text-sm mt-1"
          />
        </div>

        {/* Rodapé */}
        <div className="px-6 py-3 border-t bg-muted/20 flex items-center justify-between gap-2 shrink-0">

          <div className="text-[11px] text-muted-foreground flex items-center gap-2 min-w-0">
            {isExternalMode ? (
              <>
                <Globe2 className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {normalizedUrl ?? "URL inválida"}
                </span>
              </>
            ) : selectedPage ? (
              <>
                <FileText className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {selectedPage.title}
                  {selectedAnchor ? ` › #${selectedAnchor}` : ""}
                </span>
                {selectedAnchor && (
                  <Badge variant="outline" className="text-[10px] shrink-0">
                    seção
                  </Badge>
                )}
              </>
            ) : (
              <span>Escolha um destino ou cole uma URL</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditingExisting && (
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={handleRemove}
              >
                Remover link
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleConfirm} disabled={!canConfirm}>
              {isEditingExisting ? "Salvar" : "Inserir"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Subcomponentes visuais internos
// ---------------------------------------------------------------------------

function BrowserColumn({
  title, icon, headerRight, children,
}: {
  title: string;
  icon?: React.ReactNode;
  headerRight?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col min-h-0 bg-background">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b bg-muted/40">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {icon}
          {title}
        </div>
        {headerRight}
      </div>
      <ScrollArea className="flex-1">
        <div className="py-1">{children}</div>
      </ScrollArea>
    </div>
  );
}

function ColumnItem({
  active, onClick, icon, label, hint, hasChildren, indent = 0, clamp,
}: {
  active?: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  label: string;
  hint?: string;
  hasChildren?: boolean;
  indent?: number;
  /** Se true, permite até 2 linhas no rótulo (usado nas seções/headings). */
  clamp?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className={`w-full text-left flex items-start gap-2 px-3 py-2 text-sm transition-colors ${
        active ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
      }`}
      style={{ paddingLeft: 12 + indent * 12 }}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className={clamp ? "line-clamp-2 leading-snug" : "truncate leading-tight"}>
          {label}
        </div>
        {hint && <div className="text-[10px] text-muted-foreground truncate">{hint}</div>}
      </div>
      {hasChildren && <span className="text-muted-foreground text-xs shrink-0 mt-0.5">›</span>}
    </button>
  );
}


function EmptyMsg({ text }: { text: string }) {
  return (
    <div className="px-3 py-4 text-xs text-muted-foreground italic">{text}</div>
  );
}
